import cn from 'classnames';
import styles from '../styles.scss';
import { getAttributes } from 'views/ProductPage/selector';
import { ProductSection } from './productSection';
import { rm, NO_ELEMENT, hasElements, withCurrentPage } from 'utils';
import { useSelector } from 'react-redux';
import { DetailedProduct } from 'models/DetailedProduct';
import { ProductAttribute } from 'models/ProductAttribute';
import { Fragment } from 'react';
import { ToolTipDialog } from 'components/Shared/ToolTip/themes/BGH-RD3';

interface ClassNameProps {
  className?: string;
}

interface TitleProps {
  title?: string;
  hideTitle?: boolean;
}

interface AttributeListProps extends ClassNameProps, TitleProps {
  handleCopy?: React.ClipboardEventHandler<HTMLHeadingElement>;
}

export const StandaloneProductAttributes = ({
  className,
  hideTitle,
  handleCopy
}: AttributeListProps) => {
  const attributes = useSelector(withCurrentPage(getAttributes));
  return (
    <ProductAttributes
      attributes={attributes}
      className={className}
      hideTitle={hideTitle}
      handleCopy={handleCopy}
    />
  );
};

interface ProductAttributeProps extends Pick<DetailedProduct, 'attributes'> {
  hideTitle?: boolean;
  className?: string;
  handleCopy?: React.ClipboardEventHandler<HTMLHeadingElement>;
}

const Tooltip = ({ helpText }: Pick<ProductAttribute, 'helpText'>) => {
  if (!helpText) {
    return NO_ELEMENT;
  }

  return (
    <span className={styles.tooltipContainer}>
      <ToolTipDialog text={helpText} />
    </span>
  );
};

export const ProductAttributes = ({
  attributes,
  hideTitle,
  className,
  handleCopy
}: ProductAttributeProps) => {
  if (!hasElements(attributes)) {
    return NO_ELEMENT;
  }

  return (
    <ProductSection hideTitle={hideTitle} title={rm('product_description_attributes')}>
      <div className={cn(className, styles.productAttributes)}>
        <table>
          <tbody>
            {attributes?.map(groups => {
              const [{ viewGroupText: groupName }] = groups;
              const hasGroups = attributes.length > 1;

              return (
                <Fragment key={groupName}>
                  {hasGroups && (
                    <tr className={styles.tableHeader}>
                      <th>{groupName}</th>
                    </tr>
                  )}
                  {groups.map(({ id, name, value, helpText }, index) => {
                    const EVEN_NUMBER = 2;
                    const isEven = index % EVEN_NUMBER === 0;

                    return (
                      <tr
                        key={id}
                        className={cn({
                          [styles.even]: isEven && hasGroups,
                          [styles.odd]: !isEven && hasGroups
                        })}
                      >
                        <th>
                          {name}
                          <Tooltip helpText={helpText} />
                        </th>
                        {id === 'customattr_EAN-nr' || id === '_artnbr' ? (
                          <td onCopy={handleCopy}>{value}</td>
                        ) : (
                          <td>{value}</td>
                        )}
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </ProductSection>
  );
};
